import { useQuery, PATH, Call } from '../../services/NetworkManager';

export function getClientDetails({ queryKey }) {
  const id = queryKey[0].split(',')[1];

  return Call({
    method: 'get',
    path: PATH.client.replace('{{clientId}}', id),
  });
}

export function getClientBankDetails({ queryKey }) {
  const id = queryKey[0].split(',')[1];

  return Call({
    method: 'get',
    path: PATH.clientBank.replace('{{clientId}}', id),
  });
}

export function getClientStats({ queryKey }) {
  const id = queryKey[0].split(',')[1];

  return Call({
    method: 'get',
    path: PATH.clientStatisticsData.replace('{{clientId}}', id),
  });
}

export function getClientTaxReportStatus({ queryKey }) {
  const parts = queryKey[0].split(',');
  const id = parts[1];
  const year = parts[2] || '2025';

  return Call({
    method: 'get',
    path: PATH.taxReportStatus.replace('{{clientId}}', id).replace('{{year}}', year),
  });
}

export function useClientDetails(clientId?: string) {
  const { data, isLoading } = useQuery(`${['client', clientId]}`, getClientDetails);
  return { data, isLoading };
}

export function useClientStats(clientId?: string) {
  const { data, isLoading, refetch } = useQuery(`${['clientInfo', clientId]}`, getClientStats);
  return { data, isLoading, refetch };
}

export function useClientBank(clientId?: string) {
  const { data, isLoading } = useQuery(`${['clientBank', clientId]}`, getClientBankDetails);
  return { data, isLoading };
}

export function useClientTaxReportStatus(clientId: string, year: number) {
  const { data, isLoading } = useQuery(
    `${['clientTaxReport', clientId, String(year)]}`,
    getClientTaxReportStatus,
  );
  return { data, isLoading };
}

export const postWithdrawMoney = ({
  clientPublicId,
  amount,
}: {
  clientPublicId: string;
  amount: string;
}) => {
  return Call({
    method: 'post',
    path: PATH.withdrawMoney,
    data: {
      clientPublicId,
      amount,
    },
  });
};

const GOLD_TRANSACTION_PURPOSES = [
  'GOLD_WITHDRAW',
  'GOLD_RECHARGE',
  'GOLD_REWARD',
  'GOLD_SAVING_DAILY',
  'GOLD_SAVING_WEEKLY',
  'GOLD_SAVING_MONTHLY',
];

const SILVER_TRANSACTION_PURPOSES = [
  'SILVER_WITHDRAW',
  'SILVER_RECHARGE',
  'SILVER_REWARD',
  'SILVER_SAVING_DAILY',
  'SILVER_SAVING_WEEKLY',
  'SILVER_SAVING_MONTHLY',
];

export function getMetalsLedger({
  clientId,
  metalType,
  currentPage,
  pageSize,
  searchPhrase,
  transactionPurposes,
  transactionTypeFilter,
  sortBy,
  sortDir,
}: {
  clientId: string;
  metalType: 'GOLD' | 'SILVER';
  currentPage: number;
  pageSize: number;
  searchPhrase: string;
  transactionPurposes: string[];
  transactionTypeFilter?: string;
  sortBy: string;
  sortDir: string;
}) {
  const filteredPurposes = transactionTypeFilter
    ? transactionPurposes.filter((purpose) => purpose === transactionTypeFilter)
    : transactionPurposes;

  return Call({
    method: 'post',
    path: PATH.metalsLedger.replace('{{clientId}}', clientId).replace('{{metalType}}', metalType),
    data: {
      currentPage,
      pageSize,
      searchPhrase: searchPhrase || '',
      searchOn: searchPhrase ? ['transactionPurpose'] : [],
      filters: [{ field: 'transactionPurpose', operation: 'IN', value: filteredPurposes }],
      sortBy,
      sortDir,
      conditions: {},
    },
  });
}

export function useMetalsLedger({
  clientId,
  tab,
  page,
  pageSize,
  sortBy,
  sortDir,
  searchPhrase,
  transactionTypeFilter,
}: {
  clientId?: string;
  tab: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: string;
  searchPhrase?: string;
  transactionTypeFilter?: string;
}) {
  const transactionPurposes =
    tab === 'DIGITAL_GOLD_SIP' ? GOLD_TRANSACTION_PURPOSES : SILVER_TRANSACTION_PURPOSES;
  const metalType = tab === 'DIGITAL_GOLD_SIP' ? 'GOLD' : 'SILVER';

  const { data, isLoading } = useQuery(
    clientId
      ? [
          'metalsLedger',
          clientId,
          tab,
          page,
          pageSize,
          sortBy,
          sortDir,
          searchPhrase,
          transactionTypeFilter,
        ]
      : ['metalsLedger', 'disabled'],
    () =>
      getMetalsLedger({
        clientId: clientId!,
        metalType,
        currentPage: page,
        pageSize,
        searchPhrase: searchPhrase || '',
        transactionPurposes,
        transactionTypeFilter,
        sortBy,
        sortDir,
      }),
    { enabled: !!clientId },
  );

  return { data, isLoading };
}

export function downloadMetalsLedgerExcel({
  clientId,
  metalType,
  transactionPurposes,
}: {
  clientId: string;
  metalType: 'GOLD' | 'SILVER';
  transactionPurposes: string[];
}) {
  return Call({
    method: 'post',
    path: PATH.downloadLedgerExcel
      .replace('{{clientId}}', clientId)
      .replace('{{metalType}}', metalType),
    data: {
      currentPage: 0,
      pageSize: 20,
      searchPhrase: '',
      searchOn: [],
      filters: [
        {
          field: 'transactionPurpose',
          operation: 'IN',
          value: transactionPurposes,
        },
      ],
      sortBy: 'id',
      sortDir: 'DESC',
      conditions: {},
    },
  });
}
export function getMetalSipDetails({ clientPublicId }: { clientPublicId: string }) {
  return Call({
    method: 'get',
    path: PATH.getMetalSipDetails.replace('{{clientPublicId}}', clientPublicId),
  });
}

export { GOLD_TRANSACTION_PURPOSES, SILVER_TRANSACTION_PURPOSES };
